import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { TracksOperationProgress } from '@fry/components/common';
import { Booking, UserBookingsStore } from '@fry/booking/lib/bookings';
import { EasBuilderTransition } from '@fry/components/common/form-builder/builder.interfaces';

import { Transaction } from '@fry/payments/lib';
import { Venue } from '@fry/booking/lib';
import { BuilderStateService } from '@fry/components/common/form-builder/builder.state.service';
import { EASButtonAction } from '@fry/components/common/buttons/buttons.service';
import { SecurityService } from '@fry/lib/security';
import { MatDialog } from '@angular/material/dialog';
import { BuilderUIFormService } from '@fry/components/common/form-builder/builder-ui-form.service';
import { EasFormDialogComponent } from '@fry/components/common/easform/easform-dialog.component';

import * as EASDialogError from '@fry/components/common/dialog/dialog.error';
import { NotifyService } from '@fry/lib/utils';
import { SYSTEM_ROLE_ID } from '@fry/lib/roles/roles.system';

@Component({
    selector: 'eas-booking-manage',
    templateUrl: './booking-manage.component.html',
    providers: [
        BuilderStateService,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BookingManageComponent implements OnInit,
                                             TracksOperationProgress {

  public booking: Booking;
  public bookingOption: any;
  // private easFormGroup: EasFormGroup;
  // private transitions: any[];
  // private state: EasBuilderState;
  public initialise$: Observable<any>;
  public transactions: Transaction[];
  public auditlog$: Observable<any[]>;

  public easFormGroup$ = this.builderState.easFormGroup$;
  public transitions$ = this.builderState.transitions$;
  public state$ = this.builderState.state$;

  public isOperationInProgress = false;

  /* Sidebar */
  venue$: Observable<Venue>;
  seat: any;

  public buttons: EASButtonAction[] = [
    {
      type: 'button',
      label: $localize `Custom changes`,
      color: 'warn',
      condition: () => this.security.hasRole(SYSTEM_ROLE_ID.ADMIN),
      click: () => {
        this.doCustomTransition().subscribe(
          () => this.notify.success('Successfully applied'),
          (err) => this.notify.error(err),
        );
      }
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private builder: BuilderUIFormService,
    private bookings: UserBookingsStore,
    public builderState: BuilderStateService,
    private security: SecurityService,
    private dialog: MatDialog,
    private notify: NotifyService,
  ) { }

  ngOnInit() {
    this.route.data.pipe(
      tap(data => this.booking = data.booking),
      switchMap(() => this.init())
    ).subscribe();
  }

  init() {
    this.bookingOption = this.booking.bookingOption;
    this.auditlog$ = this.bookings.getAuditLog(this.booking.doc._id);
    return of();
  }

  doCustomTransition(): Observable<Booking> {
    return this.getCustomTransition().pipe(
      switchMap(transition => {
        return this.builderState.collectTransitionData(transition, this.booking.doc).pipe(
          map(data => {
            return { transition, data };
          })
        );
      }),
      switchMap(data => {
        return this.bookings.customTransition(this.booking.id, data.transition, data.data);
      }),
      catchError((error) => {
        // We swallow the User canceled error as that is just canceling the
        // operation, other errors will be bubbled up.
        if (EASDialogError.isUserCanceledError(error)) { return of(null); }

        // Otherwise rethrow what we have...
        return throwError(error);
      })
    );
  }

  // FIXME: This does not handle multiple dialogs or
  //        dismissing existing ones.
  private getCustomTransition(): Observable<EasBuilderTransition> {
    const payload = { destination: this.booking.doc.workflow.state };
    return this.builder.getCustomTransitionsForm(payload,
                                                 this.bookingOption.form)
               .pipe(
                 switchMap(group => {
                   const dialog = this.dialog.open(EasFormDialogComponent, {
                     data: {
                       title: $localize `Define transition`,
                       group,
                       onSubmit: (value) => of(value),
                     }
                  });

                  // Code above relies on this method returning an Observable
                  // therefore we must return `value` from here or throw it in
                  // case of dialog error.
                  return dialog.afterClosed().pipe(
                    tap((value) => {
                      if (EASDialogError.isDialogError(value)) { throw value; }
                    })
                  );
                })
    );
  }

  isReady() {
    return true;
  }

}
